import { useCallback, useEffect, useRef, useState } from 'react';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCertificate, faDownload } from '@fortawesome/free-solid-svg-icons';
import { useDispatch } from 'react-redux';
import { setToast } from 'components/Toasts/data-access/toast-reducer';
import {
  ATACResultsResponse,
  downloadCertificate,
  generateCertificate,
  getATACResults,
  getLatestCompletedRun
} from 'data-access/atac.api';

const POLL_INTERVAL_MS = 4000;
const MAX_POLL_MS = 5 * 60 * 1000;

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? dateStr : d.toLocaleDateString();
};

interface CertificateTabProps {
  projectId: string;
  results: ATACResultsResponse | null;
  onResultsUpdated?: () => void;
}

const CertificateTab = ({ projectId, results, onResultsUpdated }: CertificateTabProps) => {
  const dispatch = useDispatch();
  const [localResults, setLocalResults] = useState<ATACResultsResponse | null>(results);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollStartRef = useRef<number | null>(null);

  useEffect(() => {
    setLocalResults(results);
  }, [results]);

  const latestRun = getLatestCompletedRun(localResults?.runs ?? []);
  const runId = latestRun?.runId ?? localResults?.latestRunId ?? null;
  const certification = latestRun?.certification;
  const pdfUrl = certification?.pdfUrl;
  const summaryCards = latestRun?.summaryCards;

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    pollStartRef.current = null;
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const pollForCertificate = useCallback(
    (targetRunId: string) => {
      stopPolling();
      pollStartRef.current = Date.now();
      setIsGenerating(true);

      pollRef.current = setInterval(async () => {
        if (pollStartRef.current && Date.now() - pollStartRef.current > MAX_POLL_MS) {
          stopPolling();
          setIsGenerating(false);
          dispatch(
            setToast({
              show: true,
              title: 'Certificate timeout',
              message: 'Taking longer than expected. Refresh to check.'
            })
          );
          return;
        }

        try {
          const updated = await getATACResults(projectId);
          const data = updated.data;
          if (data) {
            setLocalResults(data);
            const run = data.runs.find(r => r.runId === targetRunId);
            if (run?.certification?.pdfUrl) {
              stopPolling();
              setIsGenerating(false);
              onResultsUpdated?.();
              dispatch(
                setToast({
                  show: true,
                  title: 'Certificate ready',
                  message: 'Your AI Trust Certificate is ready to download.'
                })
              );
            }
          }
        } catch {
          // keep polling
        }
      }, POLL_INTERVAL_MS);
    },
    [projectId, dispatch, onResultsUpdated, stopPolling]
  );

  const handleGenerate = async () => {
    if (!runId) {
      dispatch(
        setToast({
          show: true,
          title: 'No completed run',
          message: 'Complete a trust analysis run before generating a certificate.'
        })
      );
      return;
    }

    setIsGenerating(true);
    try {
      await generateCertificate(projectId, runId);
      pollForCertificate(runId);
    } catch (err: any) {
      setIsGenerating(false);
      dispatch(
        setToast({
          show: true,
          title: 'Generation failed',
          message: err?.response?.data?.message || err?.message || 'Could not generate certificate.'
        })
      );
    }
  };

  const handleDownload = async () => {
    if (!runId) return;
    setIsDownloading(true);
    try {
      const response = await downloadCertificate(projectId, runId);
      const blob = response.data as Blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ATAC-Certificate-${certification?.certificateId ?? runId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      if (pdfUrl) {
        window.open(pdfUrl, '_blank', 'noopener,noreferrer');
      } else {
        dispatch(
          setToast({
            show: true,
            title: 'Download failed',
            message: err?.response?.data?.message || err?.message || 'Could not download certificate.'
          })
        );
      }
    } finally {
      setIsDownloading(false);
    }
  };

  if (!latestRun) {
    return (
      <div className="text-center text-muted py-5">
        Complete a trust analysis run to generate your AI Trust Certificate.
      </div>
    );
  }

  if (pdfUrl) {
    return (
      <div className="rounded-3 border p-4 bg-white" style={{ maxWidth: 560 }}>
        <h5 className="fw-semibold mb-3">✅ Certificate Ready</h5>
        <div className="small text-body-secondary mb-4">
          {certification?.certificateId && (
            <div className="mb-1">
              <strong>Certificate ID:</strong> {certification.certificateId}
            </div>
          )}
          {certification?.status && (
            <div className="mb-1">
              <strong>Status:</strong> {certification.status}
            </div>
          )}
          {summaryCards?.trustScore != null && (
            <div className="mb-1">
              <strong>Trust Score:</strong> {summaryCards.trustScore}
              {summaryCards.trustGrade ? ` (Grade: ${summaryCards.trustGrade})` : ''}
            </div>
          )}
          {certification?.generatedAt && (
            <div className="mb-1">
              <strong>Issued:</strong> {formatDate(certification.generatedAt)}
            </div>
          )}
          {certification?.expiresAt && (
            <div className="mb-1">
              <strong>Expires:</strong> {formatDate(certification.expiresAt)}
            </div>
          )}
        </div>
        <Button variant="primary" className="csa-upload-btn" disabled={isDownloading} onClick={handleDownload}>
          <FontAwesomeIcon icon={faDownload} className="me-2" />
          {isDownloading ? 'Downloading…' : 'Download Certificate PDF'}
        </Button>
      </div>
    );
  }

  return (
    <div className="rounded-3 border p-4 bg-white" style={{ maxWidth: 560 }}>
      <h5 className="fw-semibold mb-2">Certificate Generation</h5>
      <p className="text-body-secondary mb-4">
        Your trust analysis is complete. Generate the official AI Trust Certificate.
      </p>
      <Button variant="primary" className="csa-upload-btn" disabled={isGenerating} onClick={handleGenerate}>
        {isGenerating ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
            Generating...
          </>
        ) : (
          <>
            <FontAwesomeIcon icon={faCertificate} className="me-2" />
            Generate Certificate
          </>
        )}
      </Button>
      {isGenerating && <p className="text-muted small mt-3 mb-0">⏳ Generating certificate…</p>}
    </div>
  );
};

export default CertificateTab;
